import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  siteThemeColours,
  brandFonts,
  fontSize,
  fontWeights,
} from '../styles';
import { Link, Button } from './ui';
import LiveAvailability from './LiveAvailability';
import VoucherButton from './VoucherButton';
import { formatPrice } from '../utils';

const StyledPackageCard = styled.article`
  position: relative;
  animation: fadeUp 1s forwards;

  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(5%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledDeal = styled.p`
  position: absolute;
  top: 0;
  left: 0;
  display: inline-block;
  padding: 6px 12px;
  color: ${standardColours.white};
  ${fontSize(12)};
  background-color: ${({ siteTheme }) =>
    siteTheme === 'valentines'
      ? siteThemeColours.valentines.primary
      : siteTheme === 'mothersday'
        ? siteThemeColours.mothersDay.primary
        : siteTheme === 'summer'
          ? brandColours.tertiary
          : siteTheme === 'christmas'
            ? siteThemeColours.christmas.primary
            : siteTheme === 'blackfriday'
              ? brandColours.tertiary
              : brandColours.quinary};
`;

const StyledInner = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px 35px 35px;
  height: 100%;
  width: 100%;
  background-color: ${({ hasDeal, siteTheme, highlighted, alt }) =>
    hasDeal && siteTheme === 'valentines'
      ? siteThemeColours.valentines.tertiary
      : hasDeal && siteTheme === 'mothersday'
        ? siteThemeColours.mothersDay.secondary
        : hasDeal && siteTheme === 'summer'
          ? siteThemeColours.summer.primary
          : hasDeal && siteTheme === 'christmas'
            ? siteThemeColours.christmas.secondary
            : hasDeal && siteTheme === 'blackfriday'
              ? standardColours.black
              : highlighted
                ? brandColours.primary
                : alt
                  ? standardColours.white
                  : brandColours.senary};

  ${({ hasDeal, siteTheme, highlighted }) => {
    if (hasDeal && siteTheme === 'valentines') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${siteThemeColours.valentines.primary};
      `;
    } else if (hasDeal && siteTheme === 'mothersday') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${siteThemeColours.mothersDay.primary};
      `;
    } else if (hasDeal && siteTheme === 'summer') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${brandColours.tertiary};
      `;
    } else if (hasDeal && siteTheme === 'christmas') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${siteThemeColours.christmas.primary};
      `;
    } else if (hasDeal && siteTheme === 'blackfriday') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${brandColours.tertiary};
      `;
    } else if (highlighted) {
      return css`
        color: ${standardColours.white};
        border: 2px solid ${brandColours.quinary};
      `;
    }
  }}
`;

const StyledSpa = styled.p`
  margin-bottom: 6px;
  color: ${({ hasDeal, siteTheme }) =>
    hasDeal &&
    (siteTheme === 'valentines' ||
      siteTheme === 'mothersday' ||
      siteTheme === 'summer' ||
      siteTheme === 'christmas')
      ? standardColours.white
      : brandColours.quinary};
  ${fontSize(14)};

  ${minBreakpointQuery.small`
    margin-bottom: 8px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 10px;
  `}
`;

const StyledName = styled.h3`
  margin-bottom: 10px;
  ${brandFonts.secondary};
  ${fontSize(26)};
  line-height: 1.1;

  ${minBreakpointQuery.small`
    ${fontSize(28)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(30)};
  `}
`;

const StyledPointsWrapper = styled.div`
  margin: 15px 0;
`;

const StyledPoints = styled.ul`
  margin-left: 20px;
  list-style-type: disc;
`;

const StyledPoint = styled.li`
  margin-top: 6px;
  line-height: 1.5;

  &:first-child {
    margin-top: 0;
  }
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  color: ${({ hasDeal, siteTheme, highlighted }) =>
    hasDeal && siteTheme === 'valentines'
      ? siteThemeColours.valentines.primary
      : hasDeal && siteTheme === 'mothersday'
        ? brandColours.primary
        : hasDeal && siteTheme === 'summer'
          ? standardColours.white
          : hasDeal && siteTheme === 'christmas'
            ? siteThemeColours.christmas.primary
            : hasDeal && siteTheme === 'blackfriday'
              ? brandColours.tertiary
              : highlighted
                ? brandColours.septenary
                : brandColours.tertiary};
  text-decoration: underline;
`;

const StyledPriceButtons = styled.div`
  margin-top: auto;
  padding-top: 25px;
`;

const StyledPrice = styled.p`
  margin-bottom: 15px;
  color: ${({ hasDeal, siteTheme, highlighted }) =>
    hasDeal &&
    (siteTheme === 'valentines' ||
      siteTheme === 'mothersday' ||
      siteTheme === 'summer' ||
      siteTheme === 'christmas' ||
      siteTheme === 'blackfriday')
      ? standardColours.white
      : highlighted
        ? brandColours.octonary
        : greyColours.darkerGrey};

  span {
    ${fontSize(18)};

    ${minBreakpointQuery.tiny`
      ${fontSize(20)};
    `}

    ${minBreakpointQuery.small`
      ${fontSize(22)};
    `}

    strong {
      color: ${({ hasDeal, highlighted }) =>
        hasDeal || highlighted ? standardColours.white : brandColours.primary};
      font-weight: ${fontWeights.medium};
    }
  }
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledButton = styled(Button)`
  padding-right: 16px;
  padding-left: 16px;
  white-space: nowrap;

  ${({ hasDeal, siteTheme }) => {
    if (hasDeal && siteTheme === 'valentines') {
      return css`
        background-color: ${siteThemeColours.valentines.primary};
      `;
    } else if (hasDeal && siteTheme === 'christmas') {
      return css`
        background-color: ${siteThemeColours.christmas.primary};
      `;
    }
  }}
`;

const PackageCard = ({
  id,
  spa,
  name,
  points,
  url,
  deal,
  liveAvailability,
  checkAvailability,
  availableForBooking,
  earliestDate,
  minPrice,
  higherPrice,
  isPricePerPerson,
  guestsPerPackage,
  maxGuestsPerPackage,
  maxOccupancy,
  voucherOptions,
  minQuantity,
  flags,
  highlighted,
  alt,
  yielding,
  changeSelectedPackageForBooking,
  ...props
}) => {
  const {
    spaseekers: {
      promotion: { theme: siteTheme },
    },
  } = useStaticQuery(graphql`
    query PackageCardQuery {
      spaseekers {
        promotion {
          theme
        }
      }
    }
  `);

  const canPurchase =
    (availableForBooking || (voucherOptions && voucherOptions.length)) &&
    !checkAvailability;

  const handleChangeSelectedPackageForBooking = initialPurchaseType => {
    changeSelectedPackageForBooking({
      id,
      name,
      minPrice,
      isPricePerPerson,
      liveAvailability,
      checkAvailability,
      availableForBooking,
      earliestDate,
      maxOccupancy,
      guestsPerPackage,
      maxGuestsPerPackage,
      voucherOptions,
      minQuantity,
      flags,
      yielding,
      initialPurchaseType,
    });
  };

  return (
    <StyledPackageCard {...props}>
      {deal && (
        <StyledDeal siteTheme={siteTheme}>
          {`${
            siteTheme === 'valentines'
              ? 'Valentines Deal'
              : siteTheme === 'mothersday'
                ? 'Mothers Day Deal'
                : siteTheme === 'summer'
                  ? 'Summer of Spa'
                  : siteTheme === 'christmas'
                    ? 'Festive Season'
                    : siteTheme === 'blackfriday'
                      ? 'Black Friday'
                      : 'Spa Deal'
          } - ${deal.text} ${deal.amount}`}
        </StyledDeal>
      )}
      <StyledInner
        hasDeal={deal}
        siteTheme={siteTheme}
        highlighted={highlighted}
        alt={alt}
      >
        {spa && (
          <StyledSpa hasDeal={deal} siteTheme={siteTheme}>
            <Link to={spa.url}>{spa.nameWithLocation}</Link>
          </StyledSpa>
        )}
        <StyledName>
          <Link to={url}>{name}</Link>
        </StyledName>
        {points && (
          <StyledPointsWrapper>
            <StyledPoints>
              {points.map((point, i) => (
                <StyledPoint key={`point-${i}`}>{point}</StyledPoint>
              ))}
            </StyledPoints>
          </StyledPointsWrapper>
        )}
        {!alt && (
          <StyledLink
            to={url}
            hasDeal={deal}
            siteTheme={siteTheme}
            highlighted={highlighted}
          >
            View full details
          </StyledLink>
        )}
        {liveAvailability && (
          <LiveAvailability alt={(deal && siteTheme) || highlighted} />
        )}
        <StyledPriceButtons>
          {minPrice && (
            <StyledPrice
              hasDeal={deal}
              siteTheme={siteTheme}
              highlighted={highlighted}
            >
              {!alt && 'From'}{' '}
              <span>
                {higherPrice && (
                  <>
                    <s>{formatPrice(higherPrice)}</s>{' '}
                  </>
                )}
                <strong>{formatPrice(minPrice)}</strong>
              </span>{' '}
              {!alt && `per ${isPricePerPerson ? 'person' : 'package'}`}
            </StyledPrice>
          )}
          <StyledButtons>
            <StyledButton
              {...(alt && {
                to: url,
              })}
              {...(changeSelectedPackageForBooking && {
                onClick: () => handleChangeSelectedPackageForBooking('booking'),
              })}
              alt={true}
              arrow={!alt}
              hasDeal={deal}
              siteTheme={siteTheme}
              highlighted={highlighted}
            >
              {!canPurchase ? 'Contact us' : alt ? 'View details' : 'Buy now'}
            </StyledButton>
            {flags.isVoucher && (
              <VoucherButton
                altTheme={(deal && siteTheme) || highlighted}
                {...(changeSelectedPackageForBooking && {
                  onClick: () =>
                    handleChangeSelectedPackageForBooking('voucher'),
                })}
              />
            )}
          </StyledButtons>
        </StyledPriceButtons>
      </StyledInner>
    </StyledPackageCard>
  );
};

export default PackageCard;
